import { EventClickArg } from '@fullcalendar/core'
import { DateClickArg } from '@fullcalendar/interaction'
import { VisitType } from '@hb/shared'
import { useCallback, useMemo, useRef, useState } from 'react'
import { VisitsCalendarContext } from './context'
import { useCalendarVisitDrafts, useCalendarVisits } from './hooks'
import { CreateEventOverlayRef, NewVisitArgs } from './types'

export const VisitsCalendarProvider = ({
  children,
  practiceId,
}: {
  children: React.ReactNode
  practiceId: string | null
}) => {
  const createEventOverlayRef = useRef<CreateEventOverlayRef>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [newVisitArgs, setNewVisitArgs] = useState<{
    patientId: string
    practiceId: string
    visitType: VisitType
    date: string
  } | null>(null)
  const onSelectDate = useCallback((date: string) => {
    setSelectedDate(date)
  }, [])
  const onSelectNewVisit = useCallback((args: NewVisitArgs) => {
    setNewVisitArgs(args)
    createEventOverlayRef.current?.close()
  }, [])

  const [start, setStart] = useState<number | null>(null)
  const [end, setEnd] = useState<number | null>(null)
  const handleDatesSet = useCallback(({ start, end }: { start: Date; end: Date }) => {
    setStart(start.getTime())
    setEnd(end.getTime())
  }, [])

  const visitsData = useCalendarVisits(practiceId, start, end)
  const visitDraftsData = useCalendarVisitDrafts(practiceId, start, end)
  const { data: visits } = visitsData
  const { data: visitDrafts } = visitDraftsData

  const events = useMemo(() => {
    const visitEvents = Object.entries(visits ?? {}).map(([id, visit]) => ({
      id,
      title: `${visit.patientFirst} ${visit.patientLast}`,
      start: visit.startTime,
      end: visit.endTime,
      type: 'visit' as const,
    }))
    const visitDraftEvents = Object.entries(visitDrafts ?? {}).map(([id, visit]) => ({
      id,
      title: `${visit.patientFirst} ${visit.patientLast}`,
      start: visit.startTimeUnix,
      end: visit.endTimeUnix,
      type: 'visit-draft' as const,
    }))
    return [...visitEvents, ...visitDraftEvents]
  }, [visits, visitDrafts])

  const handleDateClick = useCallback((info: DateClickArg) => {
    createEventOverlayRef.current?.openNewVisit(info.dayEl, info.dateStr)
  }, [])

  const handleOpenVisit = useCallback(
    (dateEl: HTMLElement, id: string) => {
      const visit = visits?.[id]
      if (!visit) return
      createEventOverlayRef.current?.openVisit(dateEl, { ...visit, id })
    },
    [visits],
  )

  const handleOpenVisitDraft = useCallback(
    (dateEl: HTMLElement, id: string) => {
      const visitDraft = visitDrafts?.[id]
      if (!visitDraft) return
      createEventOverlayRef.current?.openVisitDraft(dateEl, { ...visitDraft, id })
    },
    [visitDrafts],
  )

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const { type } = info.event.extendedProps
      switch (type) {
        case 'visit':
          handleOpenVisit(info.el, info.event.id)
          break
        case 'visit-draft':
          handleOpenVisitDraft(info.el, info.event.id)
          break
      }
    },
    [handleOpenVisit, handleOpenVisitDraft],
  )

  const contextData = useMemo(
    () => ({
      selectedDate,
      onSelectDate,
      onSelectNewVisit,
      newVisitArgs,
      onCloseNewVisit: () => {
        setNewVisitArgs(null)
      },
      practiceId,
      handleDateClick,
      handleEventClick,
      handleDatesSet,
      events,
      visits: visitsData,
      visitDrafts: visitDraftsData,
      createEventOverlayRef,
    }),
    [
      selectedDate,
      onSelectDate,
      onSelectNewVisit,
      newVisitArgs,
      practiceId,
      handleDateClick,
      handleEventClick,
      handleDatesSet,
      events,
      visitsData,
      visitDraftsData,
      createEventOverlayRef,
    ],
  )

  return (
    <VisitsCalendarContext.Provider value={contextData}>{children}</VisitsCalendarContext.Provider>
  )
}
